
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ActiveCampaign } from '../types';
import { Play, Pause, BarChart, Calendar, Flag, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useCampaigns } from '@/hooks';

const InactiveCampaignsSection: React.FC = () => {
  const { campaigns, toggleCampaignStatus } = useCampaigns();
  const [activeTab, setActiveTab] = useState('active');
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    // Check if there's a newly activated campaign from state
    if (location.state?.activatedCampaign) {
      toast({
        title: "Campaign Added",
        description: `Your new campaign with ID ${location.state.activatedCampaign.globalCampaignId} has been added to active campaigns.`
      });
    }
  }, [location.state, toast]);

  const handleToggleCampaignStatus = async (id: string) => {
    try {
      const updatedCampaign = await toggleCampaignStatus(id);
      
      toast({
        title: `Campaign ${updatedCampaign.status === 'active' ? 'Activated' : 'Paused'}`,
        description: `Campaign "${updatedCampaign.title}" has been ${updatedCampaign.status === 'active' ? 'activated' : 'paused'}.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update campaign status.",
        variant: "destructive"
      });
    }
  };

  const getFilteredCampaigns = () => {
    if (activeTab === 'active') {
      return campaigns.filter(campaign => campaign.status === 'active');
    } else if (activeTab === 'paused') {
      return campaigns.filter(campaign => campaign.status === 'paused');
    } else if (activeTab === 'all') {
      return campaigns;
    }
    return [];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Campaign Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="active">Active Campaigns</TabsTrigger>
              <TabsTrigger value="paused">Paused Campaigns</TabsTrigger>
              <TabsTrigger value="all">All Campaigns</TabsTrigger>
            </TabsList>
            
            <TabsContent value={activeTab}>
              {getFilteredCampaigns().length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {getFilteredCampaigns().map(campaign => (
                    <Card key={campaign.id} className="overflow-hidden">
                      <div className={`h-1 ${
                        campaign.status === 'active' ? 'bg-green-500' : 
                        campaign.status === 'paused' ? 'bg-amber-500' : 'bg-gray-300'
                      }`}></div>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between">
                          <CardTitle className="text-lg">{campaign.title}</CardTitle>
                          <div className="flex items-center">
                            <Flag className={`h-4 w-4 mr-1 ${
                              campaign.status === 'active' ? 'text-green-500' : 
                              campaign.status === 'paused' ? 'text-amber-500' : 'text-gray-400'
                            }`} />
                            <span className="text-xs uppercase font-semibold">{campaign.status}</span>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm text-gray-500 mt-1">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>Started {format(campaign.startDate, 'MMM d, yyyy')}</span>
                          </div>
                          <div className="flex items-center">
                            <Hash className="h-4 w-4 mr-1" />
                            <span className="font-mono text-xs">{campaign.globalCampaignId}</span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-gray-50 rounded">
                            <p className="text-lg font-bold">{campaign.referralCount}</p>
                            <p className="text-xs text-gray-500">Referrals</p>
                          </div>
                          <div className="p-2 bg-gray-50 rounded">
                            <p className="text-lg font-bold">{(campaign.conversionRate * 100).toFixed(1)}%</p>
                            <p className="text-xs text-gray-500">Conversion</p>
                          </div>
                          <div className="p-2 bg-gray-50 rounded">
                            <p className="text-lg font-bold">${campaign.totalRewards}</p>
                            <p className="text-xs text-gray-500">Rewards</p>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleToggleCampaignStatus(campaign.id)}
                          >
                            {campaign.status === 'active' ? (
                              <>
                                <Pause className="h-4 w-4 mr-1" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-1" />
                                Activate
                              </>
                            )}
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1">
                            <BarChart className="h-4 w-4 mr-1" />
                            Analytics
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border rounded bg-gray-50">
                  <p className="text-gray-500 mb-4">No {activeTab} campaigns found</p>
                  <Button variant="outline">Create a Campaign</Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default InactiveCampaignsSection;
